<template>
  <b-card no-body class="mb-1">
    <b-card-header header-tag="header" class="p-1" role="tab" :class="headerClass">
      <b-button block v-b-toggle="`accordion-${flightKey}`" :variant="buttonVariant" class="text-white"
        @click="expandGroup">
        Flight {{ group.data[0].attributes.flightNumber }},
        <span v-if="group.data[0].attributes.codeShare">
          <span v-for="(codeShare, index) in group.data[0].attributes.codeShare.split(', ')" :key="index">
            {{ codeShare }}
            <span v-if="index < group.data[0].attributes.codeShare.split(', ').length - 1">,</span>
          </span>
        </span>
        from {{ group.data[0].attributes.departureAirport }} |
        {{ group.data[0].attributes.arrivalDate }} |
        {{ formatDate(group.data[0].attributes.arrivalTime) }} ⇒
        {{ formatTime(getGateArrivalTime(group.data[0].attributes)) }}
        ({{ calculateArrivalGateDelay(
          group.data[0].attributes.status,
          group.data[0].attributes.actualGateArrival,
          group.data[0].attributes.estimatedGateArrival,
          group.data[0].attributes.arrivalTime,
          group.data[0].attributes.arrivalDate
        ) }} min)

        <span v-if="group.data[0].attributes.arrivalTerminal">
          | Terminal: {{ group.data[0].attributes.arrivalTerminal }} |
          Gate: {{ group.data[0].attributes.arrivalGate }} |
          Belt: {{ group.data[0].attributes.baggage }} |
        </span>
        | Total Guests: {{ group.data.length }} |
        Checked Guests: {{ checkedCount }} |
        OWN AIR: {{ ownAirCount }} |
        Ass.Req: {{ totalRemarks }}
      </b-button>

      <b-button class="float-right ml-1" @click.stop="openAddGuestModal" variant="success">
        <i class="fas fa-user-plus"></i>
      </b-button>
      <b-i :class="group.isEditing ? 'fas fa-save' : 'fas fa-edit'" class="float-right ml-1"
        @click.stop="toggleEditSave" style="cursor: pointer;"></b-i>

      <div class="progress mt-2">
        <i class="fas fa-plane" :style="{ left: calculatePlanePosition + '%' }"></i>
        <div class="progress-bar" role="progressbar" :style="{ width: calculateProgress + '%' }">
        </div>
      </div>
    </b-card-header>

    <b-modal :id="`add-guest-modal-${flightKey}`" title="Add New Guest" size="lg" @ok="saveNewGuest">
      <b-form>
        <b-row>
          <b-col md="4">
            <b-form-group label="Cruise Line">
              <b-form-input v-model="newGuestForm.cruiseLine"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Flight Number">
              <b-form-input v-model="newGuestForm.flightNumber"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Feeder">
              <b-form-input v-model="newGuestForm.feeder" placeholder="Enter feeder"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Guest Last Name">
              <b-form-input v-model="newGuestForm.guestsLastName" placeholder="Enter last name"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="First Name">
              <b-form-input v-model="newGuestForm.firstName" placeholder="Enter first name"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Booking Number">
              <b-form-input v-model="newGuestForm.bookingNumber" placeholder="Enter booking number"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Language">
              <b-form-input v-model="newGuestForm.language" placeholder="Enter language (e.g. DEU)"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Cabin Number">
              <b-form-input v-model="newGuestForm.cabinNumber" placeholder="Enter cabin number"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Note">
              <b-form-input v-model="newGuestForm.note" placeholder="Enter note"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Remarks">
              <b-form-input v-model="newGuestForm.remarks" placeholder="Enter remarks"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Additional Info">
              <b-form-input v-model="newGuestForm.additionalInfo" placeholder="Enter additional info"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>

    <b-collapse :id="`accordion-${flightKey}`" accordion="my-accordion" role="tabpanel">
      <b-card-body v-if="isExpanded">
        <div v-if="group.isEditing">
          <b-form-group label="Cruise Line">
            <b-form-input v-model="group.cruiseLine"></b-form-input>
          </b-form-group>
          <b-form-group label="Flight Number">
            <b-form-input v-model="group.flightNumber"></b-form-input>
          </b-form-group>
          <b-form-group label="Departure Airport">
            <b-form-input v-model="group.departureAirport"></b-form-input>
          </b-form-group>
          <b-form-group label="Departure Date">
            <b-form-input v-model="group.departureDate" type="date"></b-form-input>
          </b-form-group>
          <b-form-group label="Arrival Date">
            <b-form-input v-model="group.arrivalDate" type="date"></b-form-input>
          </b-form-group>
          <b-form-group label="Arrival Time">
            <b-form-input v-model="group.arrivalTime"></b-form-input>
          </b-form-group>
          <b-form-group label="Estimated Arrival Time">
            <b-form-input v-model="group.estimatedGateArrival"></b-form-input>
          </b-form-group>
          <b-form-group label="Actual Arrival Time">
            <b-form-input v-model="group.actualGateArrival"></b-form-input>
          </b-form-group>
          <b-form-group label="Status">
            <b-form-input v-model="group.status"></b-form-input>
          </b-form-group>
          <b-form-group label="Arrival Terminal">
            <b-form-input v-model="group.arrivalTerminal"></b-form-input>
          </b-form-group>
          <b-form-group label="Arrival Gate">
            <b-form-input v-model="group.arrivalGate"></b-form-input>
          </b-form-group>
          <b-form-group label="Baggage">
            <b-form-input v-model="group.baggage"></b-form-input>
          </b-form-group>
          <b-form-group label="Buses">
            <b-form-input v-model="group.buses"></b-form-input>
          </b-form-group>
        </div>

        <b-row v-if="!group.isEditing">
          <b-col md="4" v-for="item in sortedData" :key="item.id">
            <GuestCard :guest="item" @status-changed="updateCheckedCount" @guest-updated="$emit('data-updated')" />
          </b-col>
        </b-row>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import GuestCard from '../shared/GuestCard.vue';
import { formatTime, formatDate, calculateDelay, calculatePlanePosition, calculateProgress } from '../../helper/flight-helpers.js';

export default {
  name: 'EmbarkFlightGroup',
  components: { GuestCard },
  props: {
    group: { type: Object, required: true },
    flightKey: { type: String, required: true },
    isExpanded: { type: Boolean, default: false }
  },
  data() {
    return {
      newGuestForm: {
        cruiseLine: '',
        flightNumber: '',
        feeder: '',
        guestsLastName: '',
        firstName: '',
        bookingNumber: '',
        language: '',
        cabinNumber: '',
        note: '',
        remarks: '',
        additionalInfo: ''
      }
    };
  },
  computed: {
    sortedData() {
      return this.group.data.slice().sort((a, b) => {
        const nameA = a.attributes?.guestsLastName || '';
        const nameB = b.attributes?.guestsLastName || '';
        return nameA.localeCompare(nameB);
      });
    },
    checkedCount() {
      return this.group.data.filter(g => g.checked).length;
    },
    ownAirCount() {
      return this.group.data.filter(g => g.attributes.note === 'OWN AIR').length;
    },
    totalRemarks() {
      return this.group.data.filter(g => g.attributes.remarks && g.attributes.remarks.trim() !== '').length;
    },
    calculatePlanePosition() {
      const { actualRunwayDeparture, estimatedRunwayArrival } = this.group.data[0].attributes;
      return calculatePlanePosition(actualRunwayDeparture, estimatedRunwayArrival);
    },
    calculateProgress() {
      const { actualRunwayDeparture, estimatedRunwayArrival } = this.group.data[0].attributes;
      return calculateProgress(actualRunwayDeparture, estimatedRunwayArrival);
    },
    headerClass() {
      const status = this.group.data[0].attributes.status;
      const attributes = this.group.data[0].attributes;
      const actualOrEstimated = status === 'L' || status === '' ? attributes.actualGateArrival : attributes.estimatedGateArrival;
      const delay = parseInt(calculateDelay(actualOrEstimated, attributes.arrivalTime, attributes.arrivalDate));

      return {
        'bg-success text-white': status === 'L' && delay <= 15,
        'bg-light-red text-white': status === 'L' && delay > 15,
        'bg-dark-red text-white': status === 'C',
        'bg-light-grey text-white': status === 'A',
        'bg-dark-grey text-white': status === 'A' && delay > 15,
        'bg-light-brown': status === 'S'
      };
    },
    buttonVariant() {
      const status = this.group.data[0].attributes.status;
      const attributes = this.group.data[0].attributes;
      const actualOrEstimated = status === 'L' || status === '' ? attributes.actualGateArrival : attributes.estimatedGateArrival;
      const delay = parseInt(calculateDelay(actualOrEstimated, attributes.arrivalTime, attributes.arrivalDate));
      if (status === 'L' && delay <= 15) return 'success';
      if (status === 'L' && delay > 15) return 'light-red';
      if (status === 'C') return 'dark-red';
      if (status === 'A') return 'light-grey';
      if (status === 'A' && delay > 15) return 'dark-grey';
      if (status === 'S') return 'light-brown';
      return 'info';
    }
  },
  methods: {
    formatTime,
    formatDate,
    getGateArrivalTime(attributes) {
      const status = attributes.status;
      return status === 'L' || status === '' ? attributes.actualGateArrival : attributes.estimatedGateArrival;
    },
    calculateArrivalGateDelay(status, actualGateArrival, estimatedGateArrival, arrivalTime, arrivalDate) {
      const actualOrEstimated = status === 'L' || status === '' ? actualGateArrival : estimatedGateArrival;
      return calculateDelay(actualOrEstimated, arrivalTime, arrivalDate);
    },
    expandGroup() {
      this.$emit('expand', this.flightKey);
    },
    openAddGuestModal() {
      const firstAttr = this.group.data[0]?.attributes || {};
      this.newGuestForm = {
        cruiseLine: firstAttr.cruiseLine || '',
        flightNumber: firstAttr.flightNumber || '',
        feeder: '',
        guestsLastName: '',
        firstName: '',
        bookingNumber: '',
        language: '',
        cabinNumber: '',
        note: '',
        remarks: '',
        additionalInfo: '',
        guestStatus: 'Unchecked',
        arrivalDate: firstAttr.arrivalDate || null,
        departureAirport: firstAttr.departureAirport || null,
        arrivalTerminal: firstAttr.arrivalTerminal || null,
        departureDate: firstAttr.departureDate || null,
        arrivalTime: firstAttr.arrivalTime || null,
        estimatedGateArrival: firstAttr.estimatedGateArrival || null,
        actualGateArrival: firstAttr.actualGateArrival || null
      };
      this.$bvModal.show(`add-guest-modal-${this.flightKey}`);
    },
    async saveNewGuest(bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault();

      const firstAttr = this.group.data[0]?.attributes || {};
      const shipCallId = firstAttr.shipCall?.data?.id
        || firstAttr.shipCall?.id
        || firstAttr.shipCall;

      const payload = {};
      for (const [key, val] of Object.entries(this.newGuestForm)) {
        if (val !== '' && val !== null && val !== undefined) {
          payload[key] = val;
        }
      }
      if (shipCallId) {
        payload.shipCall = shipCallId;
      }

      try {
        const response = await this.$xhr.post('/api/transfers', { data: payload });
        const createdGuest = response.data.data;
        const newGuestObj = {
          id: createdGuest.id,
          attributes: {
            ...createdGuest.attributes,
            shipCall: shipCallId ? { data: { id: shipCallId } } : undefined
          },
          checked: false,
          isEditing: false
        };
        this.group.data.push(newGuestObj);
        this.updateCheckedCount();
        this.expandGroup();
        this.$nextTick(() => {
          this.$root.$emit('bv::show::collapse', `accordion-${this.flightKey}`);
        });
        this.$bvModal.hide(`add-guest-modal-${this.flightKey}`);
      } catch (error) {
        console.error('Error adding guest via modal:', error?.response?.data || error);
      }
    },
    toggleEditSave() {
      if (this.group.isEditing) {
        this.saveFlightChanges();
      } else {
        this.editFlight();
      }
    },
    editFlight() {
      this.expandGroup();
      this.$root.$emit('bv::show::collapse', `accordion-${this.flightKey}`);
      // Store the initial state of flight details to allow editing
      this.$set(this.group, 'cruiseLine', this.group.data[0].attributes.cruiseLine);
      this.$set(this.group, 'flightNumber', this.group.data[0].attributes.flightNumber);
      this.$set(this.group, 'departureAirport', this.group.data[0].attributes.departureAirport);
      this.$set(this.group, 'departureDate', this.group.data[0].attributes.departureDate);
      this.$set(this.group, 'arrivalDate', this.group.data[0].attributes.arrivalDate);
      this.$set(this.group, 'arrivalTime', this.group.data[0].attributes.arrivalTime);
      this.$set(this.group, 'estimatedGateArrival', this.group.data[0].attributes.estimatedGateArrival);
      this.$set(this.group, 'actualGateArrival', this.group.data[0].attributes.actualGateArrival);
      this.$set(this.group, 'arrivalTerminal', this.group.data[0].attributes.arrivalTerminal);
      this.$set(this.group, 'status', this.group.data[0].attributes.status);
      this.$set(this.group, 'arrivalGate', this.group.data[0].attributes.arrivalGate);
      this.$set(this.group, 'baggage', this.group.data[0].attributes.baggage);
      this.$set(this.group, 'buses', this.group.data[0].attributes.buses);

      this.$set(this.group, 'isEditing', true);
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', `accordion-${this.flightKey}`);
      });
    },
    async saveFlightChanges() {
      const updateData = {
        cruiseLine: this.group.cruiseLine,
        flightNumber: this.group.flightNumber,
        departureAirport: this.group.departureAirport,
        arrivalDate: this.group.arrivalDate,
        departureDate: this.group.departureDate,
        arrivalTime: this.group.arrivalTime,
        estimatedGateArrival: this.group.estimatedGateArrival,
        actualGateArrival: this.group.actualGateArrival,
        arrivalTerminal: this.group.arrivalTerminal,
        status: this.group.status,
        arrivalGate: this.group.arrivalGate,
        baggage: this.group.baggage,
        buses: this.group.buses
      };

      for (const flight of this.group.data) {
        try {
          await this.$xhr.put(`/api/transfers/${flight.id}`, {
            data: updateData
          });
          Object.assign(flight.attributes, updateData); // Update the local data to reflect changes
        } catch (error) {
          console.error('Error saving flight changes:', error);
        }
      }

      this.$set(this.group, 'isEditing', false);
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', `accordion-${this.flightKey}`);
      });
    },
    updateCheckedCount() {
      this.$emit('counts-updated', this.flightKey);
    }
  }
};
</script>
